"use client";
import {
  Briefcase,
  MapPin,
  Users,
  Building2,
  MessageSquare,
  ThumbsUp,
  TrendingUp,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/shadcnComponents/ui/tabs";
import {
  IDistributionItem,
  IEngagedAudienceAnalysisProps,
} from "../CampaignInterface/LiveCampaignInterface";
import { useState } from "react";

interface StatisticCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgColor: string;
  iconColor: string;
}

interface DistributionCardProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  iconBgColor: string;
  iconColor: string;
  borderColor: string;
  gradientFrom: string;
  gradientTo: string;
  data: Array<{
    name: string;
    value: number;
    color: string;
    numericalValues: number;
  }>;
}

const StatisticBadge = ({
  icon,
  label,
  value,
  bgColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bgColor: string;
}) => (
  <div
    className={`flex items-center space-x-2 ${bgColor} px-3 py-1 rounded-full`}
  >
    {icon}
    <span className="text-sm">
      {value} {label}
    </span>
  </div>
);

const StatisticCard = ({
  icon,
  label,
  value,
  iconColor,
}: StatisticCardProps) => (
  <div className="bg-white rounded-lg p-3 shadow-sm">
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className="flex gap-1 items-center">
      <div className={`h-4 w-4 ${iconColor} mr-2`}>{icon}</div>
      <span className="font-medium text-sm text-gray-900">{value}</span>
    </div>
  </div>
);

const DistributionCard = ({
  title,
  subtitle,
  icon,
  iconBgColor,
  iconColor,
  borderColor,
  gradientFrom,
  gradientTo,
  data,
}: DistributionCardProps) => (
  <div className={`bg-white rounded-xl border ${borderColor} overflow-hidden`}>
    <div
      className={`px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r ${gradientFrom} ${gradientTo}`}
    >
      <div className="flex items-center">
        <div
          className={`w-8 h-8 rounded-full ${iconBgColor} flex items-center justify-center mr-3`}
        >
          <div className={`h-4 w-4 ${iconColor}`}>{icon}</div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{title}</h4>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      {/* <ChevronRight className="h-5 w-5 text-gray-400" /> */}
    </div>
    <div className="p-5 space-y-4">
      {data.map((item, index) => (
        <div key={index} className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-700">{item.name}</span>
            <span className="font-medium text-gray-900">
              {item.numericalValues}
            </span>
          </div>
          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${item.value}%`,
                backgroundColor: item?.color,
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DistributionAnalysisCard = ({
  distribution,
}: {
  distribution: {
    role: IDistributionItem[];
    location: IDistributionItem[];
    industries: IDistributionItem[];
    seniority: IDistributionItem[];
  };
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {distribution?.role?.length > 0 && (
        <DistributionCard
          title="Job Roles"
          subtitle="Who's engaging with your content"
          icon={<Briefcase className={`h-4 w-4`} />}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-500"
          borderColor="border-blue-100"
          gradientFrom="from-blue-50"
          gradientTo="to-cyan-50"
          data={distribution?.role || []}
        />
      )}
      {distribution?.location?.length > 0 && (
        <DistributionCard
          title="Locations"
          subtitle="Where your audience is based"
          icon={<MapPin className={`h-4 w-4`} />}
          iconBgColor="bg-cyan-100"
          iconColor="text-cyan-500"
          borderColor="border-cyan-100"
          gradientFrom="from-cyan-50"
          gradientTo="to-blue-50"
          data={distribution?.location || []}
        />
      )}
      {distribution?.industries?.length > 0 && (
        <DistributionCard
          title="Industries"
          subtitle="Professional sectors represented"
          icon={<Building2 className={`h-4 w-4`} />}
          iconBgColor="bg-green-100"
          iconColor="text-green-500"
          borderColor="border-green-100"
          gradientFrom="from-green-50"
          gradientTo="to-emerald-50"
          data={distribution?.industries || []}
        />
      )}
      {distribution?.seniority?.length > 0 && (
        <DistributionCard
          title="Seniority Levels"
          subtitle="Career stages of your audience"
          icon={<Users className={`h-4 w-4`} />}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-500"
          borderColor="border-purple-100"
          gradientFrom="from-purple-50"
          gradientTo="to-indigo-50"
          data={distribution?.seniority || []}
        />
      )}
    </div>
  );
};

export function EngagedAudienceAnalysis({
  metrics,
  overall,
  likeDistribution,
  commentDistribution,
}: IEngagedAudienceAnalysisProps) {
  const [currentTab, setCurrentTab] = useState<"likes" | "comments">("likes");
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">
          Engaged Audience Analysis
        </h2>
        <p className="text-gray-500">
          Understanding who interacts with your content
        </p>
        <div className="flex items-center mt-2 space-x-4">
          {metrics?.er > 0 && (
            <StatisticBadge
              icon={<div className="w-2 h-2 rounded-full bg-blue-500" />}
              label="Engagement Rate"
              value={`${metrics?.er?.toFixed(2)}%`}
              bgColor="bg-blue-50"
            />
          )}
          {metrics?.likes > 0 && (
            <StatisticBadge
              icon={<ThumbsUp className="w-3.5 h-3.5 text-purple-500" />}
              label="Likes"
              value={`${metrics?.likes}`}
              bgColor="bg-purple-50"
            />
          )}
          {metrics?.comments > 0 && (
            <StatisticBadge
              icon={<MessageSquare className="w-3.5 h-3.5 text-cyan-500" />}
              label="Comments"
              value={`${metrics?.comments}`}
              bgColor="bg-cyan-50"
            />
          )}
        </div>
      </div>

      {/* Key Insights Panel */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start space-x-4">
          <div className="bg-white p-3 rounded-full shadow-sm">
            <TrendingUp className="h-6 w-6 text-gray-700" />
          </div>
          <div className="space-y-3 flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              Audience Snapshot
            </h3>
            <p className="text-gray-600">{overall?.summary}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {overall?.topRole && (
                <StatisticCard
                  icon={<Briefcase className="w-4 h-4" />}
                  label="Top Role"
                  value={overall?.topRole}
                  bgColor="bg-white"
                  iconColor="text-blue-500"
                />
              )}
              {overall?.topLocation && (
                <StatisticCard
                  icon={<MapPin className="w-4 h-4" />}
                  label="Top Location"
                  value={overall?.topLocation}
                  bgColor="bg-white"
                  iconColor="text-cyan-500"
                />
              )}
              {overall?.topIndustry && (
                <StatisticCard
                  icon={<Building2 className="w-4 h-4" />}
                  label="Top Industry"
                  value={overall?.topIndustry}
                  bgColor="bg-white"
                  iconColor="text-green-500"
                />
              )}
              {overall?.topSeniority && (
                <StatisticCard
                  icon={<Users className="w-4 h-4" />}
                  label="Top Seniority"
                  value={overall?.topSeniority}
                  bgColor="bg-white"
                  iconColor="text-purple-500"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Audience Distribution Analysis */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Audience Distribution
          </h3>
          <Tabs defaultValue="likes" className="w-auto">
            <TabsList className="h-8 p-0.5 bg-gray-100">
              <TabsTrigger
                onClick={() => setCurrentTab("likes")}
                value="likes"
                className="text-xs h-7 px-3 rounded-md data-[state=active]:bg-white"
              >
                Likes
              </TabsTrigger>
              <TabsTrigger
                onClick={() => setCurrentTab("comments")}
                value="comments"
                className="text-xs h-7 px-3 rounded-md data-[state=active]:bg-white"
              >
                Comments
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {currentTab === "likes" && (
          <DistributionAnalysisCard distribution={likeDistribution} />
        )}
        {currentTab === "comments" && (
          <DistributionAnalysisCard distribution={commentDistribution} />
        )}
      </div>
    </div>
  );
}
